import { NarrativeTextField } from '@/components/molecules';
import { FOLLOWER_ABILITIES } from '@/data/followers/skills';
import type { MoveRef } from '@/schemas/character';
import { isNarrativeFieldVisible, type Move, type NarrativeField } from '@/schemas/move';

/**
 * Editor dei `narrativeFields` (FI-18 Fase 2) di una mossa: per ogni field
 * renderizza un `<Input>` (single-line), `<Textarea>` (multiline) o
 * `<Select>` (FI-8b: quando `oneOf` è definito → enum chiuso).
 *
 * **Validazione**: il campo `required` mostra un bordo evidenziato quando
 * vuoto (post `trim()`). La regola di completezza è applicata altrove
 * dal predicate del builder (`isMoveSetupComplete` →
 * `isSectionComplete('starting-moves')`). Anche `.` è accettato come
 * valore valido (vincolo intenzionalmente lasco — vedi `NarrativeFieldSchema`).
 *
 * **FI-8a — visibilità condizionata**: `requiresChoice` filtra i field
 * visibili in base alle selezioni del gruppo indicato.
 *
 * **FI-8b — visibilità count-aware**: `requiresChoice.minOccurrences`
 * verifica il numero di occorrenze (caso `MoveChoice.allowDuplicate: true`).
 * Gestito dentro `isNarrativeFieldVisible`.
 *
 * Estratto come sotto-componente di `MoveSetupEditor` per restare entro i
 * limiti `max-lines: 400` / `max-lines-per-function: 100`.
 */
interface MoveNarrativeFieldsFieldProps {
  move: Move;
  values: Record<string, string>;
  onChange: (fieldId: string, value: string) => void;
  /**
   * **FI-8a**: selezioni correnti dei `ChoiceGroup` della mossa, usate per
   * filtrare i `narrativeFields` con `requiresChoice` (es. specie custom
   * visibile solo se "altro" è selezionato).
   */
  selectedChoices?: MoveRef['selectedChoices'];
  selectedChoiceGroups?: MoveRef['selectedChoiceGroups'];
}

export function MoveNarrativeFieldsField({
  move,
  values,
  onChange,
  selectedChoices = [],
  selectedChoiceGroups,
}: MoveNarrativeFieldsFieldProps) {
  const visibleFields = (move.narrativeFields ?? []).filter((f) =>
    isNarrativeFieldVisible(f, selectedChoices, selectedChoiceGroups),
  );
  if (visibleFields.length === 0) return null;

  return (
    <div className="grid gap-3">
      {visibleFields.map((field) => (
        <NarrativeFieldRow
          key={field.id}
          field={field}
          moveId={move.id}
          value={values[field.id] ?? ''}
          onChange={(v) => onChange(field.id, v)}
        />
      ))}
    </div>
  );
}

interface NarrativeFieldRowProps {
  field: NarrativeField;
  moveId: string;
  value: string;
  onChange: (value: string) => void;
}

/**
 * **FI-50 follow-up (2026-05-22)** — refactor SSOT: delega al molecule
 * `<NarrativeTextField>` invece di replicare il pattern shadcn Input/
 * Label/Textarea. UI ora uniforme con tutti gli altri campi narrativi
 * (nome razza custom, descrizioni equipment, ecc.).
 */
function NarrativeFieldRow({ field, moveId, value, onChange }: NarrativeFieldRowProps) {
  const inputId = `nf-${moveId}-${field.id}`;
  return (
    <NarrativeTextField
      id={inputId}
      label={field.label}
      value={value}
      onChange={onChange}
      required={field.required}
      {...(field.placeholder !== undefined ? { placeholder: field.placeholder } : {})}
      multiline={field.multiline}
      {...(field.oneOf ? { oneOf: field.oneOf, formatOneOfLabel } : {})}
    />
  );
}

/**
 * **FI-8b**: mapping ID → etichetta leggibile per le `<Select>` da
 * `NarrativeField.oneOf`. Copre i due cataloghi rilevanti per il
 * Famiglio del Mago:
 *  - 6 StatId (`forza`, `destrezza`, …) → capitalizzazione + tilde italiana
 *  - 9 FollowerAbilityId → `FOLLOWER_ABILITIES[id].label`
 *  - Fallback: capitalize + replace '-' con ' ' (utile per opzioni
 *    future non in catalogo).
 */
function formatOneOfLabel(value: string): string {
  const statLabels: Record<string, string> = {
    forza: 'Forza',
    destrezza: 'Destrezza',
    costituzione: 'Costituzione',
    intelligenza: 'Intelligenza',
    saggezza: 'Saggezza',
    carisma: 'Carisma',
  };
  if (statLabels[value]) return statLabels[value];
  if (value in FOLLOWER_ABILITIES) {
    return FOLLOWER_ABILITIES[value as keyof typeof FOLLOWER_ABILITIES].label;
  }
  return value
    .split('-')
    .map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}
